import { WithdrawalFormProps } from "@omni/types/components/props/withdrawalForm.props";
import { cleanUpObjRecursive } from "@omni/utils/object.utils";
import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useCustomerServiceApi } from "@/api/hooks/customerService.hook";
import { getPhone } from "@/components/ContactCustomerservice/helpers/formValues.helpers";
import { WithdrawalFormComponent } from "@/components/WithdrawalForm/withdrawalForm.component";
import {
    WITHDRAWAL_FAILLURE_MESSAGE_ID,
    WITHDRAWAL_SUCCESS_MESSAGE_ID
} from "@/components/WithdrawalForm/withdrawalForm.config";
import { getAddress } from "@/components/WithdrawalForm/withdrawalForm.helpers";
import { WithdrawalFormRequest } from "@/components/WithdrawalForm/withdrawalForm.types";
import { WithdrawalFormValues } from "@/components/WithdrawalForm/withdrawalFrom.schema";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import {
    Translate,
    useGranite
} from "@/redux/modules/value/translations/translations.provider";

const broadcastSuccessMessage = (t: Translate) => () =>
    broadcastMessage({
        id: WITHDRAWAL_SUCCESS_MESSAGE_ID,
        text: t("withdrawal.request.success.message"),
        status: "success",
        dismissible: true,
        activeScroll: true
    });

const broadcastFailureMessage = (t: Translate) => () =>
    broadcastMessage({
        id: WITHDRAWAL_FAILLURE_MESSAGE_ID,
        text: t("withdrawal.request.failure.message"),
        status: "danger",
        dismissible: true,
        activeScroll: true
    });

type Props = {
    className?: string;
} & WithdrawalFormProps;

export const WithdrawalForm = ({ className, termsOfServiceLink }: Props) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const [{ loading: withdrawalLoading }, sendWithdrawalRequest] =
        useCustomerServiceApi<WithdrawalFormRequest>(
            {
                url: "/withdrawalform",
                method: "POST"
            },
            { manual: true, useCache: false }
        );

    useEffect(() => {
        dispatch(CustomerModule.load());
    }, [dispatch]);

    const handleOnSubmit = async (values: WithdrawalFormValues) => {
        const withDrawalRequest: WithdrawalFormRequest = {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            phone:
                getPhone(
                    phonenumberPrefixes,
                    values.phonePrefix,
                    values.phone
                ) || undefined,
            address: getAddress(values),
            orderDate: values.orderDate.toISOString(),
            orderNumber: values.orderNumber,
            receivedOn: values.receivedOn?.toISOString(),
            withdrawalProducts: values.withdrawalProducts?.map(
                (product) => product.value
            ),
            withdrawalReason: values.withdrawalReason || undefined,
            agreedToTermsAndConditions: values.agreedToTermsAndConditions
        };

        await sendWithdrawalRequest({
            data: cleanUpObjRecursive(withDrawalRequest)
        })
            .then(broadcastSuccessMessage(t))
            .catch(broadcastFailureMessage(t));
    };

    return (
        <WithdrawalFormComponent
            className={className}
            termsOfServiceLink={termsOfServiceLink}
            submitting={withdrawalLoading}
            onSubmit={handleOnSubmit}
        />
    );
};
